import { useIsCompactViewport } from 'designSystem/hooks/useCompactViewport'
import { type DocumentTableRow } from 'features/knowledgeDocumentViewer'
import { useDocumentCardPagination } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentCardPagination'
import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'

import { DocumentResultCard } from './DocumentResultCard'

const List = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.625rem;
  overflow-y: auto;
  padding-block: 0.625rem;
  padding-inline: 0.625rem;
`

interface DocumentResultsListProps {
  filteredDocuments: DocumentTableRow[]
  totalCount: number
  filteredCount: number
  hasActiveFilters: boolean
  activeIndex: number
  onSelect: (doc: DocumentTableRow) => void
  onActiveIndexChange: (index: number) => void
}

export const DocumentResultsList: React.FC<DocumentResultsListProps> = ({
  filteredDocuments,
  totalCount,
  filteredCount,
  hasActiveFilters,
  activeIndex,
  onSelect,
  onActiveIndexChange,
}) => {
  const cardRefs = useRef<Map<number, HTMLButtonElement>>(new Map())
  const isCompact = useIsCompactViewport()

  const { pageDocuments, pageActiveIndex, pageStart } =
    useDocumentCardPagination({
      filteredDocuments,
      totalCount,
      filteredCount,
      hasActiveFilters,
      activeIndex,
      onActiveIndexChange,
    })

  useEffect(() => {
    const el = cardRefs.current.get(pageActiveIndex)
    if (!el) return
    requestAnimationFrame(() => {
      el.scrollIntoView({ block: 'nearest', behavior: 'auto' })
    })
  }, [pageActiveIndex])

  return (
    <List>
      <CardList>
        {pageDocuments.map((doc, pageIdx) => {
          const globalIdx = pageStart + pageIdx
          return (
            <DocumentResultCard
              key={doc.id}
              document={doc}
              isCompact={isCompact}
              onClick={() => onActiveIndexChange(globalIdx)}
              onDoubleClick={() => onSelect(doc)}
              cardRef={(el) => {
                if (el) cardRefs.current.set(pageIdx, el)
                else cardRefs.current.delete(pageIdx)
              }}
            />
          )
        })}
      </CardList>
    </List>
  )
}
